/**
 * Same-origin JSON client for the ADMIN process (/api/v1/admin/*, separate origin and cookie from the
 * user app). The admin session lives only in an HttpOnly cookie; nothing auth-related is stored in the
 * browser. Every mutation carries X-HopEx-Request: 1 (CSRF defence).
 */
import { formatJalali } from '@hopex/ui';

export class ApiFailure extends Error {
  readonly code: string;
  readonly status: number;
  constructor(code: string, message: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

const OFFLINE = 'ارتباط با سرور برقرار نشد. اتصال را بررسی کنید و دوباره تلاش کنید.';

async function request<T>(
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
  extra?: Record<string, string>,
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api/v1/admin${path}`, {
      method,
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        ...(method === 'POST'
          ? { 'content-type': 'application/json', 'x-hopex-request': '1' }
          : {}),
        ...extra,
      },
      body: method === 'POST' ? JSON.stringify(body ?? {}) : null,
    });
  } catch {
    throw new ApiFailure('OFFLINE', OFFLINE, 0);
  }
  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const e = (json as { error?: { code: string; message: string } } | null)?.error;
    throw new ApiFailure(e?.code ?? 'INTERNAL', e?.message ?? OFFLINE, res.status);
  }
  return json as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T = { ok: true }>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>('POST', path, body, headers),
  /** POST that returns raw bytes (KYC2 evidence: never a URL, never cached). */
  blob: async (path: string, body: unknown): Promise<Blob> => {
    let res: Response;
    try {
      res = await fetch(`/api/v1/admin${path}`, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'content-type': 'application/json', 'x-hopex-request': '1' },
        body: JSON.stringify(body),
      });
    } catch {
      throw new ApiFailure('OFFLINE', OFFLINE, 0);
    }
    if (!res.ok) {
      const json = (await res.json().catch(() => null)) as {
        error?: { code: string; message: string };
      } | null;
      throw new ApiFailure(
        json?.error?.code ?? 'INTERNAL',
        json?.error?.message ?? OFFLINE,
        res.status,
      );
    }
    return res.blob();
  },
};

/** One key per intended money command; a retry of the same intent reuses it (server dedupes). */
export const newIdempotencyKey = (): string => crypto.randomUUID();

export const errorMessage = (e: unknown): string => (e instanceof ApiFailure ? e.message : OFFLINE);

export interface AdminMe {
  adminId: string;
  username: string;
  displayName: string;
  restricted: boolean;
  permissions: string[];
  stepUpActive: boolean;
}

export interface AdminUserRow {
  id: string;
  /** Human-friendly support ID («HX-1234567»). */
  publicId: string;
  displayName: string | null;
  phoneMasked: string | null;
  status: string;
  kyc1Status: string;
  createdAt: string;
  lastActiveAt: string | null;
}

type Check = 'NOT_RUN' | 'PASSED' | 'FAILED' | 'UNAVAILABLE';

export interface AdminUserDetail {
  user: AdminUserRow;
  security: {
    totpEnabled: boolean;
    totpEnabledAt: string | null;
    totpRequired: boolean;
    recoveryCodesRemaining: number;
    phoneSource: 'OTP' | 'ADMIN' | null;
    passwordChangedAt: string | null;
    lastLoginAt: string | null;
  };
  financial: CustomerFinancial;
  effectiveLevel: 'NONE' | 'KYC1' | 'KYC2';
  kyc2Submissions: Kyc2Submission[];
  kyc2: {
    status: string;
    verificationSource: 'SYSTEM' | 'ADMIN_OVERRIDE' | null;
    reason: string | null;
    verifiedAt: string | null;
  };
  decisions: {
    id: string;
    action: string;
    oldState: unknown;
    newState: unknown;
    reason: string;
    admin: string;
    at: string;
  }[];
  kyc: {
    status: string;
    verificationSource: 'SYSTEM' | 'ADMIN_OVERRIDE' | null;
    reason: string | null;
    checks: {
      phonePossession: Check;
      mobileOwnership: Check;
      civilIdentity: Check;
      ageEligibility: Check;
    };
    identityVersion: number;
    submittedAt: string | null;
    verifiedAt: string | null;
    identity: { nationalIdMasked: string; legalName: string; birthYear: string } | null;
  } | null;
  evidence: {
    id: string;
    type: string;
    provider: string;
    providerRef: string | null;
    outcome: string;
    category: string | null;
    attempt: number;
    identityVersion: number;
    requestedAt: string;
    latencyMs: number | null;
  }[];
  transitions: { from: string | null; to: string; reason: string | null; at: string }[];
  securityEvents: {
    id: string;
    type: string;
    severity: string;
    ipPrefix: string | null;
    device: string | null;
    at: string;
  }[];
  sessions: {
    id: string;
    device: string | null;
    ipPrefix: string | null;
    createdAt: string;
    lastSeenAt: string;
    revokedAt: string | null;
    revokeReason: string | null;
    authLevel: string;
  }[];
  bankInstruments: {
    id: string;
    kind: string;
    masked: string;
    bankCode: string | null;
    ownershipStatus: string;
    ownershipSource: 'PROVIDER' | 'ADMIN_OVERRIDE' | null;
    active: boolean;
    verifiedAt: string | null;
    createdAt: string;
    lastUsedAt: string | null;
  }[];
  audit: {
    id: string;
    actorType: string;
    actorId: string | null;
    action: string;
    reason: string | null;
    at: string;
    correlationId: string | null;
  }[];
  identityVersions: {
    version: number;
    current: boolean;
    source: 'KYC1' | 'CORRECTION' | 'ADMIN_CORRECTION';
    correctionCaseId: string | null;
    legalName: string;
    nationalIdMasked: string;
    birthYear: string;
    verifiedAt: string;
    supersededAt: string | null;
  }[];
  corrections: {
    id: string;
    status: string;
    reason: string | null;
    baseVersion: number;
    resultVersion: number | null;
    createdAt: string;
    completedAt: string | null;
  }[];
  recoveries: {
    id: string;
    status: string;
    reason: string | null;
    eligibleAt: string | null;
    createdAt: string;
    completedAt: string | null;
  }[];
  restrictions: {
    id: string;
    kind: string;
    capabilities: string[];
    createdAt: string;
    expiresAt: string | null;
    liftedAt: string | null;
  }[];
}

export interface AuditRow {
  id: string;
  actorType: string;
  actorId: string | null;
  /** Phase 8: the acting admin's username (admin actors only). */
  actorName?: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  reason: string | null;
  ipPrefix: string | null;
  correlationId: string | null;
  at: string;
  before?: unknown;
  after?: unknown;
}

/** Money in the customer read model: decimal strings or null (no authoritative source → «—»). */
type Money = string | null;
export interface CustomerFinancial {
  source: 'NONE' | 'LEDGER' | 'REVIEW_FIXTURE';
  current: {
    portfolioToman: Money;
    tomanBalance: Money;
    usdtValue: Money;
    availableToman: Money;
    reservedToman: Money;
  };
  lifetime: {
    tomanDeposits: Money;
    tomanWithdrawals: Money;
    netTomanFlow: Money;
    cryptoDepositsToman: Money;
    cryptoWithdrawalsToman: Money;
    buyVolumeToman: Money;
    sellVolumeToman: Money;
    convertVolumeToman: Money;
    feesPaidToman: Money;
    transactionCount: number | null;
  };
  recent: {
    lastTransactionAt: string | null;
    pendingCount: number | null;
    pendingAmountToman: Money;
  };
}

export interface CustomerRow {
  id: string;
  publicId: string;
  displayName: string | null;
  phoneMasked: string | null;
  status: string;
  kyc1Status: string;
  kycLevel: 'NONE' | 'KYC1' | 'KYC2';
  totpEnabled: boolean;
  totpRequired: boolean;
  verifiedBanks: number;
  createdAt: string;
  lastActiveAt: string | null;
  balanceToman: Money;
  lifetimeDepositsToman: Money;
  lifetimeWithdrawalsToman: Money;
}
export interface CustomerPage {
  items: CustomerRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface KycCounters {
  kycPending: number;
  kycNeedsCorrection: number;
  kycProviderUnavailable: number;
  kycManualDecisionsToday: number;
  kycCompletedToday: number;
}
export interface Overview {
  asOf: string;
  users: {
    total: number;
    newToday: number;
    active: number;
    activeLast7Days: number;
    kyc0: number;
    kyc1: number;
    kyc2: number;
    suspended: number;
    locked: number;
  };
  queues: KycCounters & { supportCases: number | null };
  financial: {
    source: 'NONE' | 'LEDGER';
    depositsToday: Money;
    withdrawalsToday: Money;
    pendingAmount: Money;
    conversionVolume: Money;
    fees: Money;
    failedTransactions: number | null;
  };
  riskEvents: {
    id: string;
    type: string;
    severity: string;
    userId: string;
    userName: string | null;
    at: string;
  }[];
  recentDecisions: {
    id: string;
    action: string;
    reason: string;
    userId: string;
    userName: string | null;
    admin: string;
    at: string;
  }[];
}
export interface KycQueueRow {
  id: string;
  userId: string;
  displayName: string | null;
  phoneMasked: string | null;
  level: 'KYC1' | 'KYC2';
  status: string;
  issue: string | null;
  submittedAt: string | null;
  waitingSince: string;
}
export interface KycQueue {
  counters: KycCounters;
  page: number;
  pageSize: number;
  hasMore: boolean;
  items: KycQueueRow[];
}

export type ProviderLane = 'sms' | 'phoneOwnership' | 'civilIdentity' | 'bankIdentity';
/** Breaker state per adapter + whether the lane runs on a simulator (local/test mocks). */
export type ProviderHealth = Record<ProviderLane, Record<string, string>> & {
  /** Absent from older API builds → treated as a simulator (never claimed live). */
  simulated?: Record<ProviderLane, boolean>;
};

export interface BankInstrumentRow {
  id: string;
  kind: 'CARD' | 'IBAN';
  masked: string;
  bankCode: string | null;
  ownershipStatus: string;
  ownershipSource: 'PROVIDER' | 'ADMIN_OVERRIDE' | null;
  active: boolean;
  verifiedAt: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  userId: string;
  publicId: string;
  displayName: string | null;
}
export interface BankInstrumentPage {
  items: BankInstrumentRow[];
  total: number;
  page: number;
  pageSize: number;
}

/** Jalali date + time in Tehran («۱۲ مهر ۱۴۰۵، ۲۳:۳۴»); «—» when unknown. */
export const dt = (iso: string | null | undefined): string => formatJalali(iso);

export const KYC_STATUS: Record<string, string> = {
  NOT_STARTED: 'احراز انجام نشده',
  IN_PROGRESS: 'احراز انجام نشده',
  CHECKING: 'در حال بررسی',
  VERIFIED: 'تأییدشده',
  NEEDS_CORRECTION: 'نیاز به اصلاح',
  TEMPORARILY_UNAVAILABLE: 'موقتاً در دسترس نیست',
  REJECTED: 'ردشده',
};

/* ───────── Phase 4: ledger, Toman operations, reconciliation (server views, decimal strings) ───────── */

export interface Kyc2Submission {
  id: string;
  attemptNo: number;
  status: string;
  correctionTargets: string[] | null;
  decisionReason: string | null;
  rulesVersion: string;
  rulesAcceptedAt: string;
  evidence: { id: string; contentType: string; byteSize: number; sha256: string };
  submittedAt: string;
  decidedAt: string | null;
  /** OD-58: VIDEO attempts carry the Jibit biometric result (face / liveness / speech). */
  kind?: 'SELFIE' | 'VIDEO';
  biometric?: {
    state: string;
    outcome: string | null;
    faceSimilarity: string | null;
    livenessScore: string | null;
    speechSimilarity: string | null;
    category: string | null;
    spokenLine: string | null;
  } | null;
}

export interface BalanceSnapshot {
  available: string;
  reserved: string;
  total: string;
}
export interface AssetBalance extends BalanceSnapshot {
  asset: string;
  nameFa: string;
  scale: number;
}
export type AdjustmentOperation = 'CREDIT' | 'DEBIT' | 'RESERVE' | 'RELEASE' | 'REVERSAL';
export interface Adjustment {
  id: string;
  ref: string;
  operation: AdjustmentOperation;
  asset: string;
  amount: string;
  reason: string;
  internalNote: string | null;
  externalReference: string;
  admin: string | null;
  transactionId: string;
  reservationId: string | null;
  reservationStatus: string | null;
  reversesAdjustmentId: string | null;
  reversedByAdjustmentId: string | null;
  before: BalanceSnapshot;
  after: BalanceSnapshot;
  correlationId: string;
  createdAt: string;
}
export interface AdjustmentPreview {
  operation: AdjustmentOperation;
  asset: string;
  amount: string;
  before: BalanceSnapshot;
  after: BalanceSnapshot;
  target: { kind: 'RESERVATION' | 'ADJUSTMENT'; id: string; ref: string | null } | null;
  blocked: string | null;
}
export interface JournalTx {
  id: string;
  type: string;
  sourceType: string;
  sourceId: string;
  effect: string;
  actorType: string;
  actorId: string | null;
  correlationId: string;
  reversesTransactionId: string | null;
  reversedByTransactionId: string | null;
  createdAt: string;
  postings: {
    accountClass: string;
    userId: string | null;
    asset: string;
    amount: string;
    balanceAfter: string | null;
  }[];
}
export type DepositMethod = 'CARD_TO_CARD' | 'ACCOUNT_TO_ACCOUNT' | 'POL' | 'SHEBA_TRANSFER';
export interface TomanDeposit {
  id: string;
  ref: string;
  method: DepositMethod;
  status: 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED_CREDITED' | 'REJECTED' | 'CANCELLED';
  declaredAmount: string;
  finalAmount: string | null;
  source: { kind: string; masked: string; instrumentId?: string };
  destination: { label: string; holderName: string; accountId?: string };
  userReference: string | null;
  rejectReason: string | null;
  businessDate: string;
  creditTransactionId: string | null;
  createdAt: string;
  updatedAt: string;
  userId: string;
  publicId: string;
  displayName: string | null;
  version: string;
  matchedEventId: string | null;
}
export type WithdrawalStatus =
  'PROCESSING' | 'SECURITY_HOLD' | 'SETTLEMENT_UNCERTAIN' | 'COMPLETED' | 'REJECTED_REFUNDED';
export interface TomanWithdrawal {
  id: string;
  ref: string;
  status: WithdrawalStatus;
  amount: string;
  fee: string;
  destination: { kind: 'CARD' | 'IBAN'; masked: string; bankCode: string | null };
  /** The destination instrument — for the audited full-number reveal (OD-41). */
  instrumentId: string;
  eta: { tier: string } | null;
  firstToInstrument: boolean;
  rail: string | null;
  trackingRef: string | null;
  holdReason: string | null;
  uncertaintyReason: string | null;
  rejectReason: string | null;
  reserveTransactionId: string | null;
  settleTransactionId: string | null;
  confirmedAt: string | null;
  decidedAt: string | null;
  createdAt: string;
  userId: string;
  publicId: string;
  displayName: string | null;
  version: string;
}
export interface Paged<T> {
  page: number;
  pageSize: number;
  hasMore: boolean;
  counts?: Record<string, number>;
  items: T[];
}
export interface InboundEvent {
  id: string;
  ref: string;
  provider: string;
  externalEventId: string;
  channel: string;
  amount: string;
  settlementState: 'SETTLED' | 'PENDING';
  source: { kind: string; masked: string | null } | null;
  traceRef: string | null;
  providerTs: string | null;
  status: 'UNMATCHED' | 'MATCHED' | 'NEEDS_REVIEW' | 'RETURN_REQUIRED';
  resolutionReason: string | null;
  destinationLabel: string;
  depositRef: string | null;
  createdAt: string;
  version: string;
}
export interface ReconCase {
  id: string;
  ref: string;
  kind: string;
  status: 'OPEN' | 'RESOLVED';
  reasons: string[];
  details: Record<string, unknown> | null;
  resolution: string | null;
  resolutionNote: string | null;
  ledgerTransactionId: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  createdAt: string;
  version: string;
  event: {
    id: string;
    ref: string | null;
    amount: string | null;
    source: string | null;
    settlementState: string | null;
  } | null;
  deposit: {
    id: string;
    ref: string | null;
    declaredAmount: string | null;
    userId: string | null;
  } | null;
  withdrawal: {
    id: string;
    ref: string | null;
    amount: string | null;
    userId: string | null;
    version: string | null;
  } | null;
}
export interface ReconRuns {
  runs: {
    id: string;
    kind: string;
    triggeredBy: string | null;
    status: 'RUNNING' | 'CLEAN' | 'MISMATCH' | 'FAILED';
    checks: { id: string; ok: boolean; count: number }[] | null;
    mismatches: unknown;
    halted: boolean;
    startedAt: string;
    finishedAt: string | null;
  }[];
  killSwitchEvents: {
    id: string;
    action: 'HALT' | 'RELEASE';
    reason: string;
    actorType: string;
    actorId: string | null;
    runId: string | null;
    at: string;
  }[];
}
export interface FinanceOverview {
  source: 'LEDGER';
  asOf: string;
  depositsToday: string;
  withdrawalsToday: string;
  pendingDeposits: number;
  depositsInReview: number;
  pendingWithdrawals: number;
  pendingWithdrawalAmount: string;
  openCases: number;
  openEvidence: number;
  rejectedToday: number;
  userLiabilitiesToman: string;
  halted: boolean;
  haltReason: string | null;
  haltedSince: string | null;
  lastReconciliation: {
    id: string;
    status: string;
    kind: string;
    startedAt: string;
    finishedAt: string | null;
  } | null;
}
export interface CustomerLedger {
  financial: CustomerFinancial;
  balances: AssetBalance[];
  holds: {
    id: string;
    asset: string;
    amount: string;
    kind: string;
    adjustmentRef: string | null;
    reason: string | null;
    createdAt: string;
  }[];
  adjustments: Adjustment[];
  journal: JournalTx[];
  deposits: TomanDeposit[];
  withdrawals: TomanWithdrawal[];
}
export interface HopexAccount {
  id: string;
  label: string;
  bankCode: string | null;
  holderName: string;
  ibanMasked: string | null;
  cardMasked: string | null;
  accountNoMasked: string | null;
  methods: DepositMethod[];
  status: 'ACTIVE' | 'RESERVE' | 'INACTIVE';
  priority: number;
  version: string;
  createdAt: string;
}
